import { type ReactNode, useEffect } from 'react'
import {
  ArrowClockwiseIcon,
  ArrowElbowDownRightIcon,
  ErrorMessage,
  Mtds,
  OwnerId,
  Task,
  Timeline,
  toUserMessage,
  useGetOwnerNameQueries,
  useGetTaskAuditByIdQuery
} from 'prove-common'
import {
  buildDelegationHistoryEvents,
  type DelegationHistoryChange,
  type DelegationHistoryEvent
} from './delegationAuditUtils'

type DelegationAuditLogProps = {
  task: Task
}

const formatTimestamp = (timestamp: number): string =>
  new Intl.DateTimeFormat('nb-NO', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(timestamp)

const OWNER_DEPTH = {
  region: 0,
  avdeling: 1,
  seksjon: 2,
  team: 3,
  user: 0
} as const

const UnitName = ({ ownerId, name }: { ownerId: string; name: string }) => {
  const owner = OwnerId.fromString(ownerId)
  const depth = owner == null ? 0 : OWNER_DEPTH[owner.type]

  return (
    <Mtds.Flex data-align='center' data-gap={1} data-nowrap>
      {Array.from({ length: depth }, (_, index) => (
        <ArrowElbowDownRightIcon
          key={index}
          aria-hidden
          className={index === depth - 1 ? 'shrink-0' : 'invisible shrink-0'}
        />
      ))}
      <span>{name}</span>
    </Mtds.Flex>
  )
}

const ActorName = ({
  actorId,
  actorName
}: {
  actorId: string
  actorName?: string
}) => {
  if (actorName != null && actorName.length > 0) return <>{actorName}</>
  // Systemhendelser har ingen bruker å slå opp; vis alltid «System».
  if (actorId === 'system') return <>System</>
  // Brukeren er slettet eller navnet kan ikke hentes – vis en ryddig reserveverdi.
  return <>Ukjent bruker</>
}

const changeCells = (
  change: DelegationHistoryChange,
  getOwnerName: (ownerId: string) => string
): {
  unit: ReactNode
  from: string
  to: string
  change: ReactNode
} => {
  const unit = (
    <UnitName ownerId={change.ownerId} name={getOwnerName(change.ownerId)} />
  )
  if (change.type === 'created') {
    return {
      unit,
      from: '-',
      to: `${change.quantity}`,
      change: (
        <Mtds.Tag data-color='success' data-icon='none'>
          Lagt til
        </Mtds.Tag>
      )
    }
  }
  if (change.type === 'removed') {
    return {
      unit,
      from: `${change.quantity}`,
      to: '-',
      change: (
        <Mtds.Tag data-color='warning' data-icon='none'>
          Fjernet
        </Mtds.Tag>
      )
    }
  }
  if (change.type === 'owner') {
    return {
      unit,
      from: getOwnerName(change.previousOwnerId),
      to: getOwnerName(change.ownerId),
      change: (
        <Mtds.Tag data-color='warning' data-icon='none'>
          Flyttet
        </Mtds.Tag>
      )
    }
  }

  const delta = change.to - change.from
  return {
    unit,
    from: `${change.from}`,
    to: `${change.to}`,
    change: (
      <Mtds.Tag data-color={delta > 0 ? 'success' : 'neutral'} data-icon='none'>
        {delta > 0 ? `+${delta}` : delta}
      </Mtds.Tag>
    )
  }
}

const ChangeTable = ({
  changes,
  getOwnerName
}: {
  changes: DelegationHistoryChange[]
  getOwnerName: (ownerId: string) => string
}) => (
  <Mtds.Table
    aria-label='Endringer i fordelingen'
    data-align='center'
    data-fixed
    data-size='sm'
  >
    <colgroup>
      <col width='46%' />
      <col width='18%' />
      <col width='18%' />
      <col width='18%' />
    </colgroup>
    <thead>
      <tr>
        <th>Enhet</th>
        <th data-nowrap>Fra</th>
        <th data-nowrap>Til</th>
        <th data-nowrap>Endring</th>
      </tr>
    </thead>
    <tbody>
      {changes.map((change, index) => {
        const cells = changeCells(change, getOwnerName)
        return (
          <tr key={`${change.delegationId}-${change.type}-${index}`}>
            <td>{cells.unit}</td>
            <td data-nowrap>{cells.from}</td>
            <td data-nowrap>{cells.to}</td>
            <td data-nowrap>{cells.change}</td>
          </tr>
        )
      })}
    </tbody>
  </Mtds.Table>
)

const HistoryEventCard = ({
  event,
  getOwnerName
}: {
  event: DelegationHistoryEvent
  getOwnerName: (ownerId: string) => string
}) => (
  <Mtds.Flex data-gap={4} data-nowrap>
    <Timeline data-color='info' icon={<ArrowClockwiseIcon />} showLine={true} />
    <Mtds.Details
      data-align='start'
      data-variant='card'
      className='mb-6 w-full'
      open
    >
      <Mtds.Details.Summary>
        {formatTimestamp(event.timestamp)} ·{' '}
        <ActorName actorId={event.actorId} actorName={event.actorName} />
      </Mtds.Details.Summary>
      <ChangeTable changes={event.changes} getOwnerName={getOwnerName} />
    </Mtds.Details>
  </Mtds.Flex>
)

export const DelegationAuditLog = ({ task }: DelegationAuditLogProps) => {
  const audit = useGetTaskAuditByIdQuery(task.id)
  const events = buildDelegationHistoryEvents(
    Array.isArray(audit.data) ? audit.data : []
  )
  const ownerIds = [
    ...new Set(
      events.flatMap((event) =>
        event.changes.flatMap((change) =>
          change.type === 'owner'
            ? [change.ownerId, change.previousOwnerId]
            : [change.ownerId]
        )
      )
    )
  ]
  const ownerNames = useGetOwnerNameQueries(ownerIds)

  useEffect(() => {
    if (audit.isError) {
      Mtds.toast.danger(
        toUserMessage(audit.error, 'hente fordelingshistorikken')
      )
    }
  }, [audit.error, audit.isError])

  if (audit.isLoading) return <Mtds.Skeleton />
  if (audit.isError) return <ErrorMessage error={audit.error} />
  if (events.length === 0) {
    return <Mtds.Muted>Ingen fordelingshistorikk tilgjengelig.</Mtds.Muted>
  }

  const getOwnerName = (ownerId: string): string =>
    ownerNames.data.find((owner) => owner.id === ownerId)?.name ?? ownerId

  return (
    <Mtds.Grid data-gap={0}>
      {events.map((event) => (
        <HistoryEventCard
          key={event.key}
          event={event}
          getOwnerName={getOwnerName}
        />
      ))}
    </Mtds.Grid>
  )
}
